<script lang="ts" setup>
import { ProductResponse } from '@/services/models/ProductResponse'
import { StepsController } from '@/controllers/StepsController'
import EnlargeBox from '@/components/EnlargeBox.vue'
import { CharacteristicType, ModalResultType, RecommendationBadgeType, SalesPackageType } from '@/helper/Types'
import { ModalTypes } from '@/helper/ModalTypes'
import { modalService } from '@/services/modals/ModalService'
import ModalParams from '@/services/modals/ModalParams'
import { ref } from 'vue'
import { copyToClipboard, writeJsonToClipboard } from '@/helper/tools'
import RecommendationBadge from '@/components/badges/RecommendationBadge.vue'
import { BackupHeaterCharacteristic } from '@/services/models/BackupHeaterCharacteristic.ts'
import InfoBadge from '@/components/badges/InfoBadge.vue'
import BackupHeaterPowerOutput from '@/components/steps/components/BackupHeaterPowerOutput.vue'
import { BackupHeater } from '@/services/models/implementations/BackupHeater.ts'

export type ProductColumn = {
  cssClass: string
  columnName: string
  valCb: (val: ProductResponse) => string
}

const props = defineProps<{
  products: ProductResponse[]
  columns: ProductColumn[]
  unselectable: boolean
  productName: string
  hasNoHeader?: boolean
  detailsInModal?: boolean
  modelValue?: string | number
  recommendationType?: RecommendationBadgeType
  preselectedProduct?: BackupHeater | undefined
  preselectedProductInfo?: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: ProductResponse['id']): void
}>()

const el = ref<HTMLElement | null>(null)
const enlargeBoxId = ref<string>('')

function productClicked(e: Event): void {
  if (!props.unselectable) {
    return
  }
  const $target = e.currentTarget as HTMLInputElement | HTMLLabelElement

  if (('control' in $target ? ($target.control as HTMLInputElement) : $target).checked) {
    e.preventDefault()
    emit('update:modelValue', '')
  }
}

function valueChanged(id: string): void {
  emit('update:modelValue', id)
  StepsController.currentStepValid(false)
}

function detailsClicked(e: Event, refId: string): void {
  if (props.detailsInModal) {
    modalService
      .showModal(
        new ModalParams(
          ModalTypes.PRODUCT_DETAILS,
          props.products.find(p => p.id === refId)
        )
      )
      .then(modalResult => {
        if (modalResult.resultType === ModalResultType.CONFIRM) {
          const id = modalResult.payload?.id
          if (id) {
            valueChanged(id)
          }
        }
      })

    return
  }

  enlargeBoxId.value = enlargeBoxId.value === refId ? '' : refId
}

function getBackupHeaterTotalCoverage(product: ProductResponse) {
  const backupHeater = product.characteristics.find(c => c.type === CharacteristicType.BACKUP_HEATER) as BackupHeaterCharacteristic
  return backupHeater?.totalHeatLossCoveragePercent || 0
}

</script>

<template>
  <div ref="el" class="fTable is--products-table card has--selects">
    <div v-if="!hasNoHeader" class="fTable__tr hide-mobile">
      <div v-for="(column, i) in columns" :key="i" :class="column.cssClass" class="fTable__th column">
        <span>{{ $t(column.columnName) }}</span>
      </div>
    </div>

    <div
      v-if="preselectedProduct"
      class="fTable__item is--select"
      :class="{ 'preselected-item': preselectedProduct }"
    >
      <div class="fTable__tr fTable__header">
        <input
          :checked="true"
          type="radio"
        >
        <span class="preselected-product form__cm " />
        <span class="form__cm-label" />
        <div class="fTable__tr fTable__cb">
          <div :class="columns[0].cssClass" class="fTable__td column">
            <div class="fTable__product-infos">
              <span>{{ columns[0].valCb(preselectedProduct) }}</span>
              <span v-for="(column, index) in columns.slice(1)" :key="index" class="is--component-spec hide-desktop">
                {{ $t(column.columnName) }}: {{ column.valCb(preselectedProduct) }}
              </span>
              <span v-if="preselectedProductInfo" class="preselected-product__info"> {{ preselectedProductInfo }}</span>
            </div>
          </div>
          <div
            v-for="(column, j) in columns.slice(1)"
            :key="j"
            :class="column.cssClass"
            class="fTable__td column hide-mobile"
          >
            <span>{{ column.valCb(preselectedProduct) }}</span>
          </div>
          <button
            :class="{ 'is--open': enlargeBoxId === preselectedProduct.id }"
            class="btn is--raw fTable__details-btn"
            type="button"
            @click.prevent.stop="detailsClicked($event, preselectedProduct.id)"
          >
            <span>{{ $t('step_components.table_details') }}</span>
            <i class="ic-chevron-down" />
          </button>
        </div>
      </div>
      <div v-if="preselectedProduct" class="fTable is--products-table has--selects preselected-product__wrapper">
        <div class="is--product column">
          <BackupHeaterPowerOutput :integrated-backup-heater="preselectedProduct" />
        </div>
      </div>

      <EnlargeBox
        v-if="preselectedProduct"
        :key="`enlarge-box-${preselectedProduct.id}`"
        :is-open="enlargeBoxId === preselectedProduct.id"
        class="fTable__tr fTable__details details"

      >
        <template #content>
          <div class="fTable__td column is--product">
            <div class="details__image-box image-box is--zoomed has--brand-bg">
              <div :class="!preselectedProduct.pictureUrl && 'fallback-bg'" class="image-box__container">
                <img
                  v-if="preselectedProduct.pictureUrl" :alt="preselectedProduct.name" :src="preselectedProduct.pictureUrl"
                  loading="lazy">
              </div>
            </div>
          </div>
          <div class="column">
            <ul v-if="preselectedProduct.productAdvantages" class="check-list product__features">
              <li v-for="index in Math.min(3, preselectedProduct.productAdvantages.length)" :key="index">
                {{ preselectedProduct.productAdvantages[index - 1] }}
              </li>
            </ul>
          </div>
        </template>
      </EnlargeBox>
    </div>

    <div
      v-for="(product, i) in products"
      :key="i"
      :data-id="product.id"
      class="fTable__item is--select"
      @click.exact.alt.prevent="copyToClipboard(product.id)"
      @click.exact.alt.ctrl.shift.prevent="writeJsonToClipboard(product)"
    >
      <RecommendationBadge v-if="product.recommended && recommendationType" :badge-type="recommendationType" />

      <div class="fTable__tr fTable__header">
        <input
          :id="productName + i"
          :checked="modelValue === product.id"
          :class="{ firstfocus: !i }"
          :data-value="product.id"
          :name="productName"
          :value="product.id"
          type="radio"
          @change="valueChanged(product.id)"
          @keyup="productClicked"
        >
        <span class="form__cm" />
        <label :for="productName + i" class="form__cm-label" @click="productClicked" />
        <label :for="productName + i" class="fTable__tr fTable__cb" @click="productClicked">
          <div :class="columns[0].cssClass" class="fTable__td column">
            <div class="fTable__product-infos">
              <span>{{ columns[0].valCb(product) }}</span>
              <p v-if="getBackupHeaterTotalCoverage(product) > 0" class="form__info">
                {{ $t('step_components.buh_coverage', { percent: Math.ceil(getBackupHeaterTotalCoverage(product)) }) }}</p>
              <InfoBadge
                v-if="product.salesPackageType === SalesPackageType.SALES_PACKAGE"
                :info-text="$t('step_sale_set.badge_set')"
                :tooltip-text="$t('step_sale_set.badge_set_tooltip')"
              />
              <span v-for="(column, index) in columns.slice(1)" :key="index" class="is--component-spec hide-desktop">
                {{ $t(column.columnName) }}: {{ column.valCb(product) }}
              </span>
            </div>
          </div>
          <div
            v-for="(column, j) in columns.slice(1)"
            :key="j"
            :class="column.cssClass"
            class="fTable__td column hide-mobile"
          >
            <span>{{ column.valCb(product) }}</span>
          </div>
          <button
            :class="{ 'is--open': enlargeBoxId === product.id }"
            class="btn is--raw fTable__details-btn"
            type="button"
            @click.prevent.stop="detailsClicked($event, product.id)"
          >
            <span>{{ $t('step_components.table_details') }}</span>
            <i class="ic-chevron-down" />
          </button>
        </label>
      </div>

      <EnlargeBox
        :key="`enlarge-box-${product.id}`"
        :is-open="enlargeBoxId === product.id"
        class="fTable__tr fTable__details details"
      >
        <template #content>
          <div class="fTable__td column is--product">
            <div class="details__image-box image-box is--zoomed has--brand-bg">
              <div :class="!product.pictureUrl && 'fallback-bg'" class="image-box__container">
                <img v-if="product.pictureUrl" :alt="product.name" :src="product.pictureUrl" loading="lazy">
              </div>
            </div>
          </div>
          <div class="column">
            <ul v-if="product.productAdvantages" class="check-list product__features">
              <li v-for="index in Math.min(3, product.productAdvantages.length)" :key="index">
                {{ product.productAdvantages[index - 1] }}
              </li>
            </ul>
          </div>
        </template>
      </EnlargeBox>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';

.card {
  overflow: visible;
}

.fTable > .fTable__tr:first-child {
  @include themify($themes) {
    border-radius: rem-calc(themed('cardBorderRadius')) rem-calc(themed('cardBorderRadius')) 0 0;
  }
}

.fTable__item:last-child .fTable__header {
  overflow: hidden;

  @include themify($themes) {
    border-radius: 0 0 rem-calc(themed('cardBorderRadius')) rem-calc(themed('cardBorderRadius'));
  }
}

label {
  .column {
    @include media('<desktop') {
      align-items: center;
      flex: 1;
    }

    @include media('<tablet') {
      justify-content: center;
    }
  }

  .form__info {
    margin: 0;
    word-break: break-word;
    hyphens: auto;
  }

  &.fTable__tr.fTable__cb {
    display: flex;
    align-items: center;

    .fTable__details-btn {
      transform: unset;
      position: relative;
      right: unset;
      top: unset;
      margin: 1rem 2rem 1rem 1rem;
      flex: 0;

      @include media('<tablet') {
        margin-right: 1rem;
      }
    }
  }
}

div {
  .column {
    @include media('<desktop') {
      align-items: center;
      flex: 1;
    }

    @include media('<tablet') {
      justify-content: center;
    }
  }

  .form__info {
    margin: 0;
    word-break: break-word;
    hyphens: auto;
  }

  &.fTable__tr.fTable__cb {
    display: flex;
    align-items: center;

    .theme-va & {
      background: $vaSelectSelectedBg;
    }

    .theme-sd & {
      background: $sdSelectSelectedBg;
    }

    .fTable__details-btn {
      transform: unset;
      position: relative;
      right: unset;
      top: unset;
      margin: 1rem 2rem 1rem 1rem;
      flex: 0;

      @include media('<tablet') {
        margin-right: 1rem;
      }
    }
  }
}

.preselected-product {
  background: $preselectedColor !important;
  border-color: $preselectedColor !important;

  &__info {
    color: $textColor50575C;
    font-size: 0.875rem;
    font-style: normal;
    font-weight: 400;
    line-height: 1.125rem;
  }

  &__wrapper {
    .theme-va & {
      background: $vaSelectSelectedBg;
    }

    .theme-sd & {
      background: $sdSelectSelectedBg;
    }
  }
}

</style>
